import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Spin, Alert, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { getPreparationById } from '../../services/preparations';
import { resolveMedia } from '../../utils/media';
import '../../styles/global.css';

const { Title, } = Typography;

const PreparationViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError('ID no proporcionado');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getPreparationById(id, true);
        setData(result);
      } catch (err) {
        console.error('Error al cargar la mini-rutina:', err);
        setError('No se pudo cargar la información de la mini-rutina');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleEdit = () => {
    navigate(`/preparations/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/preparations/all');
  };

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleBack}>
              Volver
            </Button>
          }
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Datos no encontrados"
          description="Los datos solicitados no existen o no se pudieron cargar."
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={handleBack}>
              Volver
            </Button>
          }
        />
      </div>
    );
  }

  const ExternalMediaRenderer = ({ mediaPath, style = {} }) => {
    if (!mediaPath) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#999',
          border: '1px dashed #ddd',
          borderRadius: '8px'
        }}>
          No hay multimedia disponible
        </div>
      );
    }

    const getFullUrl = (path) => {
      if (!path) return null;
      if (path.startsWith('http://') || path.startsWith('https://')) return path;
      return `https://${path}`;
    };

    const fullUrl = getFullUrl(mediaPath);

    const getMediaType = (url) => {
      if (!url) return 'unknown';
      const pathWithoutParams = url.split('?')[0];
      const extension = pathWithoutParams.split('.').pop().toLowerCase();
      
      const videoExtensions = ['mp4', 'mov', 'avi', 'webm', 'ogg', 'm4v', 'mpg'];
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      
      if (videoExtensions.includes(extension)) return 'video';
      if (imageExtensions.includes(extension)) return 'image';
      return 'unknown';
    };

    const mediaType = getMediaType(fullUrl);
    const defaultStyle = { 
      maxWidth: '100%', 
      maxHeight: '400px', 
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    };

    switch (mediaType) {
      case 'video':
        return (
          <video controls style={{ ...defaultStyle, ...style }} preload="metadata">
            <source src={fullUrl} type="video/mp4" />
            Tu navegador no soporta el elemento de video.
          </video>
        );
      case 'image':
        const srUrl = resolveMedia(mediaPath);
        return (
          <img 
            src={srUrl} 
            alt="Contenido multimedia"
            style={{ ...defaultStyle, ...style }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        );
      default:
        return (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: 'var(--sthenus-primary)',
            border: '1px dashed var(--sthenus-primary)',
            borderRadius: '8px'
          }}>
            Tipo de archivo no soportado o URL inválida
            <div style={{ fontSize: '12px', marginTop: '8px' }}>{mediaPath}</div>
          </div>
        );
    }
  };

  return (
    <div style={{ padding: 24, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined style={{ fontSize: '24px' }} />} 
            onClick={handleBack}
            style={{ padding: 0, color: '#262626', height: 'auto' }}
          />
          <Title level={3} style={{ marginBottom: 0 }}>Ver Mini-Rutina</Title>
        </div>

        <Button 
          type="text" 
          icon={<EditOutlined style={{ fontSize: '24px' }} />} 
          onClick={handleEdit}
          style={{ padding: 0, color: '#262626', height: 'auto' }}
        />
      </div>

      <Card style={{ borderRadius: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <ExternalMediaRenderer mediaPath={data.media_path} />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Row gutter={16}>
              <Col xs={24} md={24} className="col-spaced">
                <div className="field-label">Título Mini-Rutina</div>
                <div className="field-value-gray">{data.title || 'No disponible'}</div>
              </Col>
              <Col xs={24} md={12} className="col-spaced">
                <div className="field-label">Tipo</div>
                <div className="field-value-gray">{data.type?.name || 'No disponible'}</div>
              </Col>
              {/* <Col xs={24} md={12} className="col-spaced">
                <div className="field-label">Descripción Corta</div>
                <div className="field-value-gray">{data.short_description || 'No disponible'}</div>
              </Col> */}
            </Row> 
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          {/* <Col xs={24} md={24} className="col-spaced">
            <div className="field-label">Descripción Detallada</div>
            <div 
              className="field-value-gray"
              dangerouslySetInnerHTML={{ __html: data.description || 'No disponible' }}
            />
          </Col> */}
          
          <Col xs={24} md={24} className="col-spaced" style={{ marginTop: 16 }}>
            <div className="field-label" style={{ marginBottom: 12 }}>Ejercicios Configurados</div>
            <div className="field-value-gray">
              {data.exercises && data.exercises.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <Row gutter={16} style={{ fontWeight: '600', color: '#595959' }}>
                      <Col xs={10}>Ejercicio</Col>
                      <Col xs={8}>Tipo de Medición</Col>
                      <Col xs={6}>Valor</Col>
                    </Row>
                  </li>
                  {data.exercises.map((item) => (
                    <li key={item.id} style={{ padding: '8px 0' }}>
                      <Row gutter={16} align="middle">
                        <Col xs={10}>
                          <a 
                            href={`/exercises/view/${item.exercise?.id || item.exercise_id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/exercises/view/${item.exercise?.id || item.exercise_id}`);
                            }}
                            style={{ 
                              fontSize: '15px',
                              fontWeight: '600',
                              color: 'var(--sthenus-primary, #1890ff)',
                              textDecoration: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            {item.exercise?.title || item.title || 'Ejercicio no especificado'}
                          </a>
                        </Col>
                        <Col xs={8} style={{ color: '#595959', fontSize: '14px' }}>
                          {item.measurement_type?.name || item.measurement_type_label || 'No especificado'}
                        </Col>
                        <Col xs={6} style={{ color: '#262626', fontSize: '14px', fontWeight: '500' }}>
                          {item.value}
                        </Col>
                      </Row>
                    </li>
                  ))}
                </ul>
              ) : (
                'Sin ejercicios registrados'
              )}
            </div>
          </Col>
        </Row>     
      </Card>
    </div>
  );
};

export default PreparationViewPage;